import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { sellPosition } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ProtectProfitForm } from "@/components/ProtectProfitForm";

const fmt = (v) => (v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const SellDialog = ({ item, onClose, onSold, onOrdersChanged }) => {
  const [shares, setShares] = useState("");
  const [price, setPrice] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (item) { setShares(String(item.shares)); setPrice(String(item.current_price ?? "")); }
  }, [item]);

  if (!item) return null;
  const isShort = item.position_type === "short";
  const n = parseFloat(shares);
  const px = parseFloat(price);
  const valid = n > 0 && n <= item.shares && px > 0;
  const pnl = valid ? ((isShort ? item.buy_price - px : px - item.buy_price) * n) : 0;
  const remaining = valid ? +(item.shares - n).toFixed(8) : item.shares;

  const submit = async () => {
    setBusy(true);
    try {
      const res = await sellPosition(item.id, px, n);
      const t = res.data?.trade;
      toast.success(`${isShort ? "Covered" : "Sold"} ${n} ${item.symbol} at $${fmt(px)} — P&L ${t.pnl >= 0 ? "+" : "-"}$${fmt(Math.abs(t.pnl))}`);
      onSold();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to sell");
    }
    setBusy(false);
  };

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-bt-surface border-bt-border-subtle text-bt-text-primary w-[calc(100%-1.5rem)] sm:max-w-sm" data-testid="sell-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">{isShort ? "Cover" : "Sell"} {item.symbol}</DialogTitle>
          <DialogDescription className="text-bt-text-secondary text-xs">
            You hold {item.shares} share{item.shares === 1 ? "" : "s"}{isShort ? " (short)" : ""} · bought at ${fmt(item.buy_price)}
          </DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="now">
        <TabsList className="grid grid-cols-2 w-full bg-bt-surface-2" data-testid="sell-tabs">
          <TabsTrigger value="now" data-testid="sell-tab-now">{isShort ? "Cover now" : "Sell now"}</TabsTrigger>
          <TabsTrigger value="protect" data-testid="sell-tab-protect">Protect profit</TabsTrigger>
        </TabsList>
        <TabsContent value="protect" className="mt-3">
          <ProtectProfitForm item={item} onDone={() => { onOrdersChanged?.(); onClose(); }} />
        </TabsContent>
        <TabsContent value="now" className="mt-3">
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-bt-text-secondary">Shares to {isShort ? "cover" : "sell"}</Label>
            <div className="flex gap-2 mt-1">
              <Input type="number" min="0" max={item.shares} step="any" value={shares} onChange={(e) => setShares(e.target.value)}
                className="bg-bt-surface-2 border-bt-border-subtle font-mono" data-testid="sell-shares-input" />
              <Button type="button" variant="outline" onClick={() => setShares(String(item.shares))}
                className="border-bt-border-subtle text-bt-text-secondary font-mono text-xs" data-testid="sell-all-btn">All</Button>
            </div>
          </div>
          <div>
            <Label className="text-xs text-bt-text-secondary">Price per share</Label>
            <Input type="number" min="0" step="any" value={price} onChange={(e) => setPrice(e.target.value)}
              className="mt-1 bg-bt-surface-2 border-bt-border-subtle font-mono" data-testid="sell-price-input" />
          </div>
          <div className="bg-bt-surface-2 border border-bt-border-subtle px-3 py-2 font-mono text-xs space-y-1" data-testid="sell-preview">
            <p className="flex justify-between"><span className="text-bt-text-tertiary">Proceeds</span><span>${fmt(valid ? n * px : 0)}</span></p>
            <p className="flex justify-between"><span className="text-bt-text-tertiary">P&L</span>
              <span className={pnl >= 0 ? "text-bt-positive" : "text-bt-negative"} data-testid="sell-preview-pnl">{pnl >= 0 ? "+" : "-"}${fmt(Math.abs(pnl))}</span></p>
            <p className="flex justify-between"><span className="text-bt-text-tertiary">Remaining shares</span><span data-testid="sell-preview-remaining">{remaining}</span></p>
          </div>
          {!valid && shares !== "" && (
            <p className="text-[11px] text-bt-negative" data-testid="sell-error">Enter more than 0 and at most {item.shares} shares, and a price above $0.</p>
          )}
          <Button onClick={submit} disabled={!valid || busy} className="w-full bg-bt-negative text-white hover:bg-bt-negative/90" data-testid="sell-confirm-btn">
            {busy ? "Processing…" : valid ? `${isShort ? "Cover" : "Sell"} ${n} share${n === 1 ? "" : "s"}` : isShort ? "Cover" : "Sell"}
          </Button>
        </div>
        </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
